// Editor för ButtonBlock — text, länk, justering och färger.

import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ButtonBlock, TextAlign } from '../types';

type Props = {
  block: ButtonBlock;
  onChange: (patch: Partial<ButtonBlock>) => void;
};

// Liten färgväljare med både color-picker och text-input bredvid, så användaren
// kan klistra in egna hex-värden.
const ColorRow = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <Stack direction='row' spacing={1} alignItems='center'>
    <Box
      component='input'
      type='color'
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{ width: 40, height: 40, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0, cursor: 'pointer' }}
    />
    <TextField label={label} value={value} onChange={(e) => onChange(e.target.value)} size='small' sx={{ flex: 1 }} />
  </Stack>
);

export const ButtonBlockEditor = ({ block, onChange }: Props) => {
  const { t } = useTranslation('groupMessage');
  return (
    <Stack spacing={2}>
      <TextField
        label={t('builder.editors.buttonLabel') as string}
        value={block.label}
        onChange={(e) => onChange({ label: e.target.value })}
        fullWidth
        size='small'
      />
      <TextField
        label={t('builder.editors.buttonHref') as string}
        value={block.href}
        onChange={(e) => onChange({ href: e.target.value })}
        fullWidth
        size='small'
        placeholder='https://...'
      />
      <Box>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
          {t('builder.editors.align')}
        </Typography>
        <ToggleButtonGroup
          value={block.align}
          exclusive
          size='small'
          onChange={(_, value: TextAlign | null) => {
            if (value) onChange({ align: value });
          }}
          fullWidth
        >
          <ToggleButton value='left' aria-label={t('builder.editors.alignLeft') as string}>
            <FormatAlignLeftIcon fontSize='small' />
          </ToggleButton>
          <ToggleButton value='center' aria-label={t('builder.editors.alignCenter') as string}>
            <FormatAlignCenterIcon fontSize='small' />
          </ToggleButton>
          <ToggleButton value='right' aria-label={t('builder.editors.alignRight') as string}>
            <FormatAlignRightIcon fontSize='small' />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <ColorRow label={t('builder.editors.bgColor') as string} value={block.bgColor ?? '#222222'} onChange={(v) => onChange({ bgColor: v })} />
      <ColorRow label={t('builder.editors.textColor') as string} value={block.textColor ?? '#ffffff'} onChange={(v) => onChange({ textColor: v })} />
    </Stack>
  );
};

export default ButtonBlockEditor;
